import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivateFn, CanMatchFn, Route, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { RolesService } from 'app/core/auth/roles/roles.service';

@Injectable({ providedIn: 'root' })
export class RoleGuardService {
  private roles = inject(RolesService);
  private router = inject(Router);
  private authService = inject(AuthService);

  canAccess(permission?: string | string[]): boolean | UrlTree {
    // First check if user is authenticated
    if (!this.authService.isUserAuthenticated()) {
      console.log('RoleGuard: User not authenticated, deferring to auth fail');
      return this.router.parseUrl('/auth-fail');
    }

    // Then check permissions
    if (!permission) {
      return true;
    }

    let allowed = false;
    if (Array.isArray(permission)) {
      // Require all permissions (AND semantics)
      allowed = permission.every((p) => this.roles.canAccess(p));
      console.log(`RoleGuard: Permissions [${permission.join(', ')}] - ${allowed ? 'ALLOWED' : 'DENIED'}`);
    } else {
      allowed = this.roles.canAccess(permission);
      console.log(`RoleGuard: Permission '${permission}' - ${allowed ? 'ALLOWED' : 'DENIED'}`);
    }

    return allowed || this.router.parseUrl('/unauthorized');
  }
}

/**
 * Shared logic for checking role permissions.
 */
const checkPermission = (route: ActivatedRouteSnapshot | Route): boolean | UrlTree => {
  const guard = inject(RoleGuardService);
  const permission = route.data?.['permission'] as string | string[] | undefined;
  return guard.canAccess(permission);
};

export const roleCanActivate: CanActivateFn = (route) => checkPermission(route);

export const roleCanMatch: CanMatchFn = (route) => checkPermission(route);
